import secureApi from '../../utils/secureApi';

const ENGAGEMENT_ADMIN_BASE = '/api/v1/sfl-tv/engagement/admin';

type EngagementScope = 'season' | 'match' | 'tournament';
type EngagementDocType = 'tournament' | 'season' | 'match';

export interface VotingCampaignListParams {
  page?: number;
  limit?: number;
  type?: 'voting' | 'prediction';
  scope?: EngagementScope | string;
  docType?: EngagementDocType;
  seasonId?: string;
  tournamentId?: string;
  ageCategoryId?: string;
  matchId?: string;
  status?: string;
  includeInternal?: boolean;
}

export interface EngagementCampaign {
  _id?: string;
  id?: string;
  name?: string;
  type?: string;
  scope?: string;
  status?: string;
  enabled?: boolean;
  startTime?: string;
  endTime?: string;
  rewardPolicyId?: string;
  tournamentId?: string;
  seasonId?: string;
  matchId?: string;
  sourceCampaignId?: string;
  ageCategoryId?: string;
  config?: Record<string, any>;
  presentation?: Record<string, any>;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateVotingCampaignPayload {
  name: string;
  type: 'voting' | 'prediction';
  scope: EngagementScope;
  seasonId?: string;
  tournamentId?: string;
  ageCategoryId?: string;
  matchId?: string;
  startTime?: string;
  endTime?: string;
  rewardPolicyId?: string;
  config?: Record<string, any>;
  presentation?: Record<string, any>;
}

export type UpdateVotingCampaignPayload = Partial<CreateVotingCampaignPayload> & {
  action?: 'publish' | 'activate' | 'lock' | 'complete' | 'archive';
  enabled?: boolean;
};

export interface EngagementQuestionOption {
  id: string;
  label: string;
  meta?: Record<string, any>;
}

export interface EngagementQuestion {
  _id?: string;
  id?: string;
  question?: string;
  type?: string;
  status?: string;
  correctAnswer?: string | null;
  submissionStatus?: 'open' | 'not_open' | 'closed' | string;
  canSubmit?: boolean;
  submissionMessage?: string;
  options?: EngagementQuestionOption[];
  enabled?: boolean;
  awardCategoryId?: string;
  scope?: string;
  matchId?: string;
  startTime?: string;
  lockTime?: string;
  sortNo?: number;
  config?: Record<string, any>;
}

export interface EngagementAwardCategory {
  _id?: string;
  id?: string;
  name?: string;
  title?: string;
  label?: string;
  code?: string;
  enabled?: boolean;
  status?: string;
  ageCategory?: string | Record<string, any>;
  ageCategoryId?: string;
  ageCategoryName?: string;
  ageCategoryLabel?: string;
  ageCategories?: Array<string | Record<string, any>>;
}

export interface AwardCategoryListParams {
  seasonId?: string;
  tournamentId?: string;
  sport?: string;
}

export interface CreateAwardCategoryPayload {
  tournament: string;
  tournamentSeason: string;
  title: string;
  image: string;
  resultImage?: string;
  startDate: string;
  endDate: string;
  sortNo: number;
  ageCategories?: Array<{
    ageCategory: string;
    image?: string;
    resultImage?: string;
    videoUrl?: string;
  }>;
}

export interface CreateEngagementQuestionPayload {
  question: string;
  type: 'single_choice' | 'multiple_choice' | 'numeric' | 'boolean' | 'rating' | 'text';
  options?: EngagementQuestionOption[];
  awardCategoryId?: string;
  scope?: string;
  matchId?: string;
  startTime?: string;
  lockTime?: string;
  sortNo?: number;
  config?: Record<string, any>;
}

export type UpdateEngagementQuestionPayload = Partial<CreateEngagementQuestionPayload> & {
  enabled?: boolean;
  status?: string;
};

export interface EngagementSetting {
  _id?: string;
  id?: string;
  targetType?: 'sport' | 'tournament' | 'season' | 'match' | string;
  targetId?: string;
  enabled?: boolean;
}

export interface VotingCampaignListResponse {
  campaigns: EngagementCampaign[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  hasNext?: boolean;
}

function unwrapResponse(response: any) {
  return response?.data?.data ?? response?.data ?? response;
}

function extractArrayPayload(data: any, keys: string[] = []) {
  if (Array.isArray(data)) return data;

  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
  }

  const candidates = [
    data?.paginatedResults,
    data?.results,
    data?.items,
    data?.docs,
    data?.rows,
    data?.list,
  ];

  return candidates.find(Array.isArray) ?? [];
}

function extractQuestionPayload(data: any): EngagementQuestion {
  const unwrapped = unwrapResponse(data);
  if (unwrapped?.question && typeof unwrapped.question === 'object') return unwrapped.question;
  if (unwrapped?.engagementQuestion && typeof unwrapped.engagementQuestion === 'object') return unwrapped.engagementQuestion;
  return unwrapped;
}

export async function listVotingCampaigns(params: VotingCampaignListParams = {}): Promise<VotingCampaignListResponse> {
  const response = await secureApi.get(`${ENGAGEMENT_ADMIN_BASE}/campaigns`, {
    params: {
      page: params.page ?? 1,
      limit: params.limit ?? 20,
      type: params.type ?? 'voting',
      scope: params.scope,
      docType: params.docType,
      seasonId: params.seasonId,
      tournamentId: params.tournamentId,
      ageCategoryId: params.ageCategoryId,
      matchId: params.matchId,
      status: params.status,
      includeInternal: params.includeInternal,
    },
  });

  const data = unwrapResponse(response);
  const rawCampaigns: EngagementCampaign[] = extractArrayPayload(data, ['campaigns']);

  const pagination = data?.pagination ?? {};
  const totalCount = pagination.total ?? data?.totalCount ?? rawCampaigns.length;
  const currentPage = pagination.page ?? data?.currentPage ?? params.page ?? 1;
  const pageSize = params.limit ?? 20;
  const totalPages = pagination.total_pages ?? data?.totalPages ?? Math.max(1, Math.ceil(totalCount / pageSize));

  return {
    campaigns: rawCampaigns,
    totalCount,
    currentPage,
    totalPages,
    hasNext: pagination.has_next ?? pagination.hasNext,
  };
}

export async function getVotingCampaign(campaignId: string): Promise<EngagementCampaign> {
  const response = await secureApi.get(`${ENGAGEMENT_ADMIN_BASE}/campaigns/${campaignId}`);
  return unwrapResponse(response);
}

export async function createVotingCampaign(payload: CreateVotingCampaignPayload): Promise<EngagementCampaign> {
  const response = await secureApi.post(`${ENGAGEMENT_ADMIN_BASE}/campaigns`, payload);
  return unwrapResponse(response);
}

export async function updateVotingCampaign(campaignId: string, payload: UpdateVotingCampaignPayload): Promise<EngagementCampaign> {
  const response = await secureApi.patch(`${ENGAGEMENT_ADMIN_BASE}/campaigns/${campaignId}`, payload);
  return unwrapResponse(response);
}

export async function listCampaignQuestions(campaignId: string): Promise<EngagementQuestion[]> {
  const response = await secureApi.get(`${ENGAGEMENT_ADMIN_BASE}/campaigns/${campaignId}/questions`);
  const data = unwrapResponse(response);
  return extractArrayPayload(data, ['questions']);
}

export async function listAwardCategories(params: AwardCategoryListParams = {}): Promise<EngagementAwardCategory[]> {
  if (!params.tournamentId) return [];

  try {
    const response = await secureApi.get(`/api/v1/tournaments/multi/vote/award-categories/${params.tournamentId}`, {
      params: {
        tournamentSeason: params.seasonId,
      },
    });
    const data = unwrapResponse(response);
    return extractArrayPayload(data, ['awardCategories', 'categories']);
  } catch (error: any) {
    if (error?.response?.status === 404) return [];
    throw error;
  }
}

export async function createAwardCategory(payload: CreateAwardCategoryPayload): Promise<EngagementAwardCategory> {
  const response = await secureApi.post('/api/v1/tournaments/admin/award-categories/add', payload);
  const data = unwrapResponse(response);
  return data?.awardCategory ?? data?.category ?? data;
}

export async function createCampaignQuestion(campaignId: string, payload: CreateEngagementQuestionPayload): Promise<EngagementQuestion> {
  const response = await secureApi.post(`${ENGAGEMENT_ADMIN_BASE}/campaigns/${campaignId}/questions`, payload);
  return extractQuestionPayload(response);
}

export async function updateCampaignQuestion(questionId: string, payload: UpdateEngagementQuestionPayload): Promise<EngagementQuestion> {
  const response = await secureApi.patch(`${ENGAGEMENT_ADMIN_BASE}/questions/${questionId}`, payload);
  return extractQuestionPayload(response);
}

export async function setQuestionResult(questionId: string, correctAnswer: string): Promise<EngagementQuestion> {
  const response = await secureApi.post(`${ENGAGEMENT_ADMIN_BASE}/questions/${questionId}/result`, { correctAnswer });
  return extractQuestionPayload(response);
}

export async function correctQuestionResult(questionId: string, correctAnswer: string): Promise<EngagementQuestion> {
  const response = await secureApi.patch(`${ENGAGEMENT_ADMIN_BASE}/questions/${questionId}/result`, { correctAnswer });
  return extractQuestionPayload(response);
}

export async function completeEngagementCampaign(campaignId: string): Promise<EngagementCampaign> {
  const response = await secureApi.patch(`${ENGAGEMENT_ADMIN_BASE}/campaigns/${campaignId}`, { action: 'complete' });
  return unwrapResponse(response);
}

export async function getEngagementSetting(targetType: 'sport' | 'tournament' | 'season' | 'match', targetId: string): Promise<EngagementSetting | null> {
  const response = await secureApi.get(`${ENGAGEMENT_ADMIN_BASE}/settings/${targetType}/${targetId}`);
  const raw = response?.data?.data;
  return raw && typeof raw === 'object' && 'enabled' in raw ? (raw as EngagementSetting) : null;
}

export async function upsertEngagementSetting(targetType: 'sport' | 'tournament' | 'season' | 'match', targetId: string, enabled: boolean): Promise<EngagementSetting> {
  const response = await secureApi.post(`${ENGAGEMENT_ADMIN_BASE}/settings/${targetType}/${targetId}`, { enabled });
  return unwrapResponse(response);
}
